import type { MainMenuItem, MenuSectionType } from '@/shared/model';
import { MainMenuKeys, useAppFrameStore } from '@/shared/model';
import MenuItemNavLink from '@/shared/ui/components/MenuItemNavLink';
import TruentityMenu from '@/shared/ui/components/TruentityMenu';
import { Body1 } from '@/shared/ui/components/Typography';
import { Box, Divider, IconButton, Stack } from '@mui/material';
import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';

const VISIBLE_SECONDARY_MENU_ITEMS = new Map<string, boolean>([
  [MainMenuKeys.RecentPatients, true],
  [MainMenuKeys.Patients, true],
  [MainMenuKeys.MedicationManagement, true],
  [MainMenuKeys.CareManagement, true],
  // The provider queues moved out of Care Management into their own section; without this they
  // would be in the sidebar but missing from the app grid.
  [MainMenuKeys.Provider, true],
  [MainMenuKeys.UserAdmin, true],
  [MainMenuKeys.Administration, true],
  [MainMenuKeys.SystemAdmin, true],
  [MainMenuKeys.TimesheetsInvoicesAdmin, true],
  [MainMenuKeys.QuickLinksAdmin, true]
]);

type MenuSectionProps = {
  title: ReactNode;
  options: MainMenuItem[];
  onClick: () => void;
};

const MenuSection = ({ title, options, onClick }: MenuSectionProps) => {
  return (
    <Stack
      sx={{
        minWidth: 0
      }}
    >
      <Body1
        sx={{
          color: 'primary',
          fontWeight: 'bold',
          fontSize: 11.5,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          paddingX: 1,
          paddingBottom: 0.5
        }}
      >
        {title}
      </Body1>
      <Divider sx={{ marginBottom: 0.5 }} />
      {options.map((item, index) => (
        <MenuItemNavLink
          MenuItemProps={{
            dense: true,
            sx: {
              borderRadius: 1,
              minHeight: 30,
              paddingX: 1,
              gap: 1,
              '& .MuiListItemText-primary': { fontSize: 13, lineHeight: 1.3 },
              '& .MuiSvgIcon-root': { fontSize: 17 }
            }
          }}
          to={item.navigatePath}
          title={item.label}
          icon={item.icon}
          key={index}
          onClick={onClick}
        />
      ))}
    </Stack>
  );
};

type Props = {
  icon: ReactNode;
  disabled?: boolean;
};
const SecondaryMenu = ({ icon, disabled }: Props) => {
  const { mainMenuItems } = useAppFrameStore();
  const [visibleMainMenuItems, setVisibleMainMenuItems] = useState<MenuSectionType[]>([]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (mainMenuItems) {
      const secondaryMenuItems = new Map<string, MenuSectionType>();
      let adminMenuItems: MainMenuItem[] = [];

      mainMenuItems.forEach((item, key) => {
        if (VISIBLE_SECONDARY_MENU_ITEMS.get(key) && !item.hidden && item.options.length > 0) {
          // Check for 'Admin' in the key
          if (key.includes('Admin')) {
            adminMenuItems = adminMenuItems.concat(item.options);
          } else {
            secondaryMenuItems.set(key, item);
          }
        }
      });

      const adminMenuItem: MenuSectionType = {
        title: 'Administration',
        hidden: false, // or true, if you want to initially hide this section
        options: adminMenuItems
      };

      if (adminMenuItems.length > 0) {
        secondaryMenuItems.set('Administration', adminMenuItem);
      }
      setVisibleMainMenuItems(Array.from(secondaryMenuItems.values()));
    }
  }, [mainMenuItems]);

  return (
    <>
      <IconButton disabled={disabled} onClick={handleClick}>
        {icon}
      </IconButton>

      {/*
        The shared menu shell is built for a single column of rows: a 10px primary.light frame,
        10px of its own padding, and a 12px margin on every row icon.  Around an app grid that
        reads as a thick coloured box with double-spaced links, so this instance trims it back.
        `&&&` because the shell styles the paper through a descendant selector, which would
        otherwise out-specify `sx`.
      */}
      <TruentityMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              '&&&': {
                marginTop: 1,
                border: 1,
                borderColor: 'divider',
                padding: 0
              },
              '&&& .MuiMenuItem-root .MuiSvgIcon-root': { marginRight: 0 }
            }
          }
        }}
      >
        {/*
          A real grid rather than a wrapping row of fixed 300px columns: the old layout set
          `flexWrap` on a vertical Stack, which does nothing, so every section claimed 300px plus
          a 32px margin whatever it held and the panel sprawled into ragged rows.  Columns now
          size themselves and the panel stays inside the viewport.
        */}
        <Box
          sx={{
            display: 'grid',
            // Explicit counts rather than `auto-fill`: at 780px wide with a 190px minimum and the
            // gaps taken out, auto-fill could only fit three columns and left a ragged fourth.
            // One column on a phone, two on a tablet, and all four across on a wide window.
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))'
            },
            gap: 1.5,
            padding: 1,
            width: {
              xs: 'calc(100vw - 32px)',
              sm: 'min(calc(100vw - 32px), 470px)',
              lg: 'min(calc(100vw - 32px), 880px)'
            },
            maxHeight: 'min(70vh, 560px)',
            overflowY: 'auto'
          }}
        >
          {visibleMainMenuItems.map((item, index) => (
            <MenuSection key={index} title={item.title} options={item.options} onClick={handleClose} />
          ))}
        </Box>
      </TruentityMenu>
    </>
  );
};

export default SecondaryMenu;
